/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { createKbMessages } from './messages';

const styles = cssMap({
	footer: {
		display: 'flex',
		justifyContent: 'flex-end',
		paddingTop: token('space.200'),
		paddingRight: token('space.250'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.250'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
	},
	footerActions: {
		display: 'flex',
		gap: token('space.050'),
		justifyContent: 'flex-end',
	},
});

export type CreateKBCardFooterMode =
	| 'CREATE_DRAFTS'
	| 'ARTICLE_ACTIONS'
	| 'FORM_ACTIONS'
	| 'PUBLISH_ACTIONS';

type CreateKBCardFooterProps = {
	mode: CreateKBCardFooterMode;
	onCreateDrafts?: () => void;
	onSaveForLater?: () => void;
	onPublishAll?: () => void;
	onConfirm?: () => void;
	onBack?: () => void;
	onPublish?: () => void;
	isCreateDraftsLoading?: boolean;
	isConfirmLoading?: boolean;
	isPublishLoading?: boolean;
	isConfirmDisabled?: boolean;
	isPublishDisabled?: boolean;
};

export const CreateKBCardFooter = ({
	mode,
	onCreateDrafts,
	onSaveForLater,
	onPublishAll,
	onConfirm,
	onBack,
	onPublish,
	isCreateDraftsLoading,
	isConfirmLoading,
	isPublishLoading,
	isConfirmDisabled,
	isPublishDisabled,
}: CreateKBCardFooterProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const sendButtonClickedAnalytics = (buttonName: string) => {
		sendAnalyticsEvent({
			eventType: 'track',
			action: 'clicked',
			actionSubject: 'jsmKbFromScratchFooterButton',
			actionSubjectId: 'jsmKbFromScratchFooterButtonClicked',
			attributes: {
				buttonName,
				mode,
			},
		});
	};

	const handleBackClick = () => {
		sendButtonClickedAnalytics('back');
		onBack?.();
	};

	const handleConfirmClick = () => {
		sendButtonClickedAnalytics('confirm');
		onConfirm?.();
	};

	const handleSaveForLaterClick = () => {
		sendButtonClickedAnalytics('saveForLater');
		onSaveForLater?.();
	};

	const handlePublishAllClick = () => {
		sendButtonClickedAnalytics('publishAll');
		onPublishAll?.();
	};

	const handleCreateDraftsClick = () => {
		sendButtonClickedAnalytics('createDrafts');
		onCreateDrafts?.();
	};

	const handlePublishClick = () => {
		sendButtonClickedAnalytics('publish');
		onPublish?.();
	};

	return (
		<Box xcss={styles.footer} testId="create-kb-card-footer">
			{mode === 'FORM_ACTIONS' && (
				<Box xcss={styles.footerActions}>
					<Button onClick={handleBackClick} appearance="subtle" testId="create-kb-card-back">
						{formatMessage(createKbMessages.backCtaMessage)}
					</Button>
					<Button
						onClick={handleConfirmClick}
						appearance="primary"
						testId="create-kb-card-confirm"
						isLoading={isConfirmLoading}
						isDisabled={isConfirmDisabled}
					>
						{formatMessage(createKbMessages.confirmCtaMessage)}
					</Button>
				</Box>
			)}
			{mode === 'ARTICLE_ACTIONS' && (
				<Box xcss={styles.footerActions}>
					<Button
						appearance="subtle"
						onClick={handleSaveForLaterClick}
						testId="create-kb-card-save-for-later"
					>
						{formatMessage(createKbMessages.saveForLaterMessage)}
					</Button>
					<Button
						appearance="primary"
						onClick={handlePublishAllClick}
						testId="create-kb-card-publish-all"
					>
						{formatMessage(createKbMessages.publishAllMessage)}
					</Button>
				</Box>
			)}
			{mode === 'CREATE_DRAFTS' && (
				<Box xcss={styles.footerActions}>
					<Button
						onClick={handleCreateDraftsClick}
						appearance="default"
						testId="create-kb-card-create-drafts"
						isLoading={isCreateDraftsLoading}
						isDisabled={isCreateDraftsLoading}
					>
						{formatMessage(createKbMessages.ctaMessage)}
					</Button>
				</Box>
			)}
			{mode === 'PUBLISH_ACTIONS' && (
				<Box xcss={styles.footerActions}>
					<Button onClick={handleBackClick} appearance="subtle" testId="create-kb-card-back">
						{formatMessage(createKbMessages.backCtaMessage)}
					</Button>
					<Button
						appearance="primary"
						onClick={handlePublishClick}
						testId="create-kb-card-publish"
						isLoading={isPublishLoading}
						isDisabled={isPublishDisabled}
					>
						{formatMessage(createKbMessages.publishCtaMessage)}
					</Button>
				</Box>
			)}
		</Box>
	);
};
